import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { DndDropEvent, DndModule } from 'ngx-drag-drop';
import { ListGroupTask } from '@shared/modules/list/model/list-group-task.model';
import { Task } from '@shared/models/task.model';
import { ListTasksSingleTaskComponent } from './components/list-tasks-single-task/list-tasks-single-task.component';
import { NgClass } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { TranslateModule } from '@ngx-translate/core';

const TEMPLATE_ADD_MENU_ITEMS = [
  { key: 'task', translationKey: 'ProjectsDashboard.stageTasks.templateMenu.task' },
  { key: 'documentation', translationKey: 'ProjectsDashboard.stageTasks.templateMenu.documentation' },
  { key: 'finance', translationKey: 'ProjectsDashboard.stageTasks.templateMenu.finance' },
  { key: 'control', translationKey: 'ProjectsDashboard.stageTasks.templateMenu.control' },
  { key: 'message', translationKey: 'ProjectsDashboard.stageTasks.templateMenu.message' },
  { key: 'follow_up', translationKey: 'ProjectsDashboard.stageTasks.templateMenu.follow_up' },
  { key: 'appointment', translationKey: 'ProjectsDashboard.stageTasks.templateMenu.appointment' }
] as const;

export interface ListTasksTaskDropEvent {
  taskId: number;
  sourceGroupId: number;
  targetGroupId: number;
  sourceStatus: 'created' | 'completed' | 'cancelled';
  targetStatus: 'created' | 'completed' | 'cancelled';
  index: number;
}

export interface ListTasksTaskStatusEvent {
  task: Task;
  subtasksCompleted?: boolean;
  applyStatusToSubtasks?: boolean;
}

export interface ListTasksGroupEditEvent {
  group: ListGroupTask;
}

export interface ListTasksTemplateAddEvent {
  type: (typeof TEMPLATE_ADD_MENU_ITEMS)[number]['key'];
  group: ListGroupTask;
}

@Component({
  selector: 'list-tasks-group',
  templateUrl: './list-tasks-group.component.html',
  styleUrls: ['./list-tasks-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  host: { class: 'list-tasks-group' },
  imports: [DndModule, NgClass, ListTasksSingleTaskComponent, MatMenuModule, TranslateModule]
})
export class ListTasksGroupComponent {
  group = input.required<ListGroupTask>();
  disableGroupDrag = input<boolean>(false);

  taskDropped = output<ListTasksTaskDropEvent>();
  taskStatusChanged = output<ListTasksTaskStatusEvent>();
  taskClicked = output<{ task: Task; groupId: number; isProcessGroup: boolean }>();
  groupEditRequested = output<ListTasksGroupEditEvent>();
  addTask = output<ListGroupTask>();
  templateAddSelected = output<ListTasksTemplateAddEvent>();
  protected readonly templateAddMenuItems = TEMPLATE_ADD_MENU_ITEMS;
  protected readonly isProcessGroup = computed(() => !!this.group()?.data?.isProcessGroup);

  protected get canEditGroupName(): boolean {
    const group = this.group()?.taskGroup;
    return !!group && !group.immutable && Number(group.id) > 0 && !this.isProcessGroup();
  }

  protected readonly isTemplateGroup = computed(
    () => Number(this.group()?.taskGroup?.id) < 0 || this.isProcessGroup()
  );

  protected readonly groupCompletedCount = computed(() => {
    const group = this.group();
    if (group?.completedCount !== undefined && group?.completedCount !== null) {
      return Number(group.completedCount) || 0;
    }
    return (group?.completedTasks || []).length;
  });

  protected readonly groupTasksCount = computed(() => this.groupCompletedCount());

  protected readonly groupTotalCount = computed(() => {
    const group = this.group();
    if (group?.count !== undefined && group?.count !== null) {
      return Number(group.count) || 0;
    }
    if (group?.allCount !== undefined && group?.allCount !== null) {
      return Number(group.allCount) || 0;
    }
    if (group?.currentAllCount !== undefined && group?.currentAllCount !== null) {
      return Number(group.currentAllCount) || 0;
    }
    return (
      (group?.tasks || []).length +
      (group?.completedTasks || []).length +
      (group?.cancelledTasks || []).length
    );
  });

  protected onTaskDrop(event: DndDropEvent, targetStatus: 'created' | 'completed' | 'cancelled'): void {
    const payload = event.data as {
      taskId: number;
      sourceGroupId: number;
      sourceStatus: 'created' | 'completed' | 'cancelled';
    } | null;

    if (!payload) return;

    this.taskDropped.emit({
      taskId: payload.taskId,
      sourceGroupId: payload.sourceGroupId,
      targetGroupId: this.group().taskGroup.id,
      sourceStatus: payload.sourceStatus,
      targetStatus,
      index: Number.isInteger(event.index)
        ? Number(event.index)
        : this.getTargetCollection(targetStatus).length
    });
  }

  protected openAddTask(): void {
    this.addTask.emit(this.group());
  }

  protected onTaskStatusChanged(event: ListTasksTaskStatusEvent): void {
    this.taskStatusChanged.emit(event);
  }

  protected onTaskClicked(event: { task: Task; groupId: number; isProcessGroup: boolean }): void {
    this.taskClicked.emit(event);
  }

  protected onEditGroupName(): void {
    if (!this.canEditGroupName) return;
    this.groupEditRequested.emit({ group: this.group() });
  }

  protected onTemplateAddOptionSelected(key: (typeof TEMPLATE_ADD_MENU_ITEMS)[number]['key']): void {
    this.templateAddSelected.emit({ type: key, group: this.group() });
  }

  protected getTargetCollection(status: 'created' | 'completed' | 'cancelled'): Task[] {
    if (status === 'completed') return this.group().completedTasks || [];
    if (status === 'cancelled') return this.group().cancelledTasks || [];
    return this.group().tasks || [];
  }
}
